import { Helmet } from 'react-helmet';
import React, { useState, useEffect } from 'react';
import { Row, Col, Alert, Modal, Button, Spinner } from 'react-bootstrap';
import UserList from '../../components/admin/UserList';
import UserEdit from '../../components/admin/UserEdit';
import UserCreate from '../../components/admin/UserCreate';
import { getUsers, updateUser, deleteUser, createUser, getOrganizations } from './../../api';
import { Link } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import './AdminDepartment.css'

function AdminDashboard() {
  const title = 'List of All Users';
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [alert, setAlert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [departments, setDepartments] = useState([]);
  const auth = useAuth();
  const isAdmin = auth.isAdminAuth();
  const isOrgAdmin = auth.isOrgAdminAuth();
  const [isHide_ru] = useState(true);

  useEffect(() => {
    fetchOrganization();
    fetchUsers();
  }, []);

  const fetchOrganization = async () => {
    try {
      const response = await getOrganizations();
      const data = response.data;
      if (response.status !== 200) {
        throw new Error(data.message || 'Failed to fetch organization');
      }
      setOrganization(data);
      setDepartments(data.departments || []);
    } catch (error) {
      setAlert({ type: 'danger', message: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    setIsLoading(true);  // Start loading
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      setAlert({ variant: 'danger', message: 'Failed to fetch users' });
    } finally {
      setIsLoading(false);  // End loading regardless of success/failure
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowModal(true);
    setAlert(null);
  };


  const handleDeleteShow = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
    setAlert(null);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUser(selectedUser.id);
      fetchUsers();
      handleDeleteClose();
    } catch (error) {
      console.error('Error deleting project:', error);
      setAlert({ variant: 'danger', message: 'Failed to delete user' });
    }
  };

  const handleUpdateUser = async (updatedUser) => {
    try {
      await updateUser(updatedUser.id, updatedUser);
      fetchUsers();
      setShowModal(false);
      setSelectedUser(null);
      setAlert({ variant: 'success', message: 'User updated successfully' });
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create user';
      setAlert({ variant: 'danger', message: errorMessage });
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };


  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleCloseMode = () => {
    setShowModal(false);
    setShowDeleteModal(false);
    setShowCreateModal(false);
    setAlert(null);
  }

  const sortedUsers = React.useMemo(() => {
    let sortableUsers = [...users];
    if (sortConfig.key !== null) {
      sortableUsers.sort((a, b) => {

        switch (sortConfig.key) {
          case 'projects':
          case 'files':
            return sortConfig.direction === 'ascending'
              ? a[sortConfig.key] - b[sortConfig.key]
              : b[sortConfig.key] - a[sortConfig.key];
          case 'disk_space':
            // Convert sizes back to bytes for sorting
            const getBytes = (sizeStr) => {
              const units = { 'B': 1, 'KB': 1024, 'MB': 1024 ** 2, 'GB': 1024 ** 3, 'TB': 1024 ** 4 };
              const [size, unit] = sizeStr.split(' ');
              return parseFloat(size) * units[unit];
            };
            const aBytes = getBytes(a.disk_space);
            const bBytes = getBytes(b.disk_space);
            return sortConfig.direction === 'ascending'
              ? aBytes - bBytes
              : bBytes - aBytes;
          case 'vector_space':
            // Convert sizes back to bytes for sorting
            const getBytes1 = (sizeStr) => {
              const units = { 'B': 1, 'KB': 1024, 'MB': 1024 ** 2, 'GB': 1024 ** 3, 'TB': 1024 ** 4 };
              const [size, unit] = sizeStr.split(' ');
              return parseFloat(size) * units[unit];
            };
            const aBytes1 = getBytes1(a.vector_space);
            const bBytes1 = getBytes1(b.vector_space);
            return sortConfig.direction === 'ascending'
              ? aBytes1 - bBytes1
              : bBytes1 - aBytes1;
          case 'ru_cost.total':
            const keys = sortConfig.key.split('.');
            const aValue = keys.reduce((obj, key) => obj?.[key], a) || 0;
            const bValue = keys.reduce((obj, key) => obj?.[key], b) || 0;
            return sortConfig.direction === 'ascending'
              ? aValue - bValue
              : bValue - aValue;
          default:
            // Your existing sorting logic
            return sortConfig.direction === 'ascending'
              ? (a[sortConfig.key] < b[sortConfig.key] ? -1 : 1)
              : (a[sortConfig.key] < b[sortConfig.key] ? 1 : -1);
        }
      });
    }
    return sortableUsers;
  }, [users, sortConfig]);


  const filteredUsers = sortedUsers.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleCreateUser = async (newUser) => {
    try {
      let response = await createUser(newUser);
      const data = response.data;
      //console.log("data:",data.message);
      if (response.status !== 200) {
        throw new Error(data.message || 'Failed to create user');
      }
      fetchUsers();
      setShowCreateModal(false);  
      setAlert({ type: 'success', message: data.message || 'User created successfully' });

    } catch (error) {
      // Check if error response exists and has the message
      const errorMessage = error.response?.data?.message || 'Failed to create user';
      setAlert({ variant: 'danger', message: errorMessage });
    }
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <div className="container-fluid mb-5">
        <div className="row g-5 pt-3">
          <div className="col-12">
            <div className="card">
              <div className="card-header pt-3 d-flex justify-content-between bg-white align-items-center">
                <div>
                  <h4 className="text-secondary">User Management
                    {isAdmin && isHide_ru && (
                      <>
                        <Link
                          to="/admin/users_ru"
                          title="Click to see more details"
                          className="ms-2 fs-6 text-primary text-decoration-underline"
                          style={{ fontWeight: 'normal' }}
                        >
                          <i className="bi bi-info-circle ms-3"></i>
                        </Link>
                      </>
                    )}
                  </h4>
                </div>

                <div>
                  <button type="button" className="btn btn-outline-primary" onClick={() => setShowCreateModal(true)}>
                    <i className="bi bi-plus-circle me-1"></i>
                    New User</button>
                </div>
              </div>

              <div className="table-responsive position-relative">
                <Row className="justify-content-center flex-grow-1">
                  <Col>
                    {isLoading ? (
                      <div className="text-center mt-5">
                        <Spinner
                          animation="border"
                          variant="primary"
                          role="status"
                          style={{ width: "3rem", height: "3rem" }}
                        >
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="mt-2">Loading user data...</p>
                      </div>
                    ) : (
                      <Row>
                        <Col>
                          <UserList
                            users={currentUsers}
                            onEditUser={handleEditUser}
                            onDeleteUser={handleDeleteShow}
                            onSearch={handleSearch}
                            onSort={handleSort}
                            sortConfig={sortConfig}
                            currentPage={currentPage}
                            usersPerPage={usersPerPage}
                            totalUsers={filteredUsers.length}
                            paginate={paginate}
                            isAdmin={isAdmin}
                          />
                        </Col>

                      </Row>
                    )}
                  </Col>
                </Row>
              </div>
            </div>
          </div>
          <div>
            {alert && (
              <Alert variant={alert.variant} onClose={() => setAlert(null)} dismissible>
                {alert.message}
              </Alert>
            )}
          </div>
        </div >
      </div >

      <Modal show={showModal} onHide={handleCloseMode} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <UserEdit
              organization_id={organization.id}
              departments={departments}
              user={selectedUser}
              onUpdateUser={handleUpdateUser}
              onCancel={handleCloseMode}
              isAdmin={isAdmin}
              isOrgAdmin={isOrgAdmin}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          {alert && (
            <Alert variant={alert.variant} onClose={() => setAlert(null)} dismissible>
              {alert.message}
            </Alert>
          )}
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseMode} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the user "{selectedUser?.username}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseMode}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteUser}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* add Create User modal*/}
      <Modal show={showCreateModal} onHide={handleCloseMode} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {organization ? (
            <UserCreate
              organization_id={organization.id}
              departments={departments}
              onCreateUser={handleCreateUser}
              onCancel={handleCloseMode}
            />
          ) : (
            <div>Loading organization data...</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {alert && (
            <Alert variant={alert.variant} onClose={() => setAlert(null)} dismissible>
              {alert.message}
            </Alert>
          )}
        </Modal.Footer>
      </Modal>

    </>
  );
}

export default AdminDashboard;